import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Modal } from "../../components/modal";
import {
  formValuesToResidencyName,
  residencyNameToFormValues,
} from "../../components/residency-name-input";
import { SubletForm, SubletFormValues } from "../../components/sublet-form";
import { useAdditionalFields } from "../../hooks/addition-fields";
import { useGetSublet } from "../../queries/sublet";
import {
  additionalFieldDefaultFormValue,
  parseAdditionalFieldToFormValue,
  parseFormValuesToAdditionalFields,
} from "../../services/additional-fields";
import { updateSublet } from "../../services/sublet";
import { formatDate } from "../../utils/date";
import { navigateBack } from "../../utils/navigate-back";

export function SubletEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("Sublet ID is required");
  }

  const { data: sublet, isLoading: isLoadingSublet } = useGetSublet(id);

  const { data: additionalFields } = useAdditionalFields("sublet");
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [formValues, setFormValues] = useState<SubletFormValues>({
    parentId: "",
    fromDate: "",
    toDate: "",
    useCustomName: false,
    customName: "",
    tenants: [],
    additionalFields: additionalFields.reduce(
      (acc, field) => ({
        ...acc,
        [field.name]: additionalFieldDefaultFormValue(field.type),
      }),
      {},
    ),
  });

  const isLoading = isLoadingSublet || loading;

  useEffect(() => {
    if (sublet) {
      setFormValues({
        parentId: "holding" in sublet ? sublet.holding.id : sublet.lease.id,
        fromDate: formatDate(sublet.fromDate),
        toDate: sublet.toDate ? formatDate(sublet.toDate) : "",
        ...residencyNameToFormValues(sublet.customName),
        tenants:
          sublet?.tenants.map((tenant) => {
            if ("person" in tenant) {
              return {
                personId: tenant.person.id,
              };
            } else if ("organization" in tenant) {
              return {
                organizationId: tenant.organization.id,
              };
            } else {
              throw new Error("Invalid tenant");
            }
          }) || [],
        additionalFields: additionalFields.reduce(
          (acc, field) => ({
            ...acc,
            [field.name]: parseAdditionalFieldToFormValue(
              sublet?.additionalData?.[field.name],
            ),
          }),
          {},
        ),
      });
    }
  }, [sublet, additionalFields]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/register/sublet");
  };

  return (
    <Modal open onClose={() => closeModal()}>
      <SubletForm
        hasError={hasError}
        loading={isLoading}
        values={formValues}
        onCancel={() => closeModal()}
        onChange={setFormValues}
        onSubmit={() => {
          (async () => {
            setLoading(true);
            try {
              await updateSublet(id, {
                ...(formValues.parentId.startsWith("holding:")
                  ? { holdingId: formValues.parentId }
                  : { leaseId: formValues.parentId }),
                fromDate: new Date(formValues.fromDate),
                toDate: formValues.toDate
                  ? new Date(formValues.toDate)
                  : undefined,
                customName: formValuesToResidencyName(formValues),
                tenants: formValues.tenants,
                ...(formValues.additionalFields && {
                  additionalData: parseFormValuesToAdditionalFields(
                    formValues.additionalFields,
                    additionalFields,
                    true,
                  ),
                }),
              });
              Promise.all([
                queryClient.invalidateQueries({
                  queryKey: ["sublet", id],
                }),
                queryClient.invalidateQueries({
                  queryKey: ["sublets"],
                }),
              ]);
              closeModal();
            } catch (error: unknown) {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
